import { useEffect, useRef, useState } from "react";
import { loadZXing, normalizeCode } from "./barcode";

export type ScannerState =
  | { kind: "idle" }
  | { kind: "scanning" }
  | { kind: "denied" }
  | { kind: "no_camera" }
  | { kind: "error"; message: string };

interface Options {
  /** Called once per fresh, normalized decode while running (not paused).
   *  Identical back-to-back reads within DEBOUNCE_MS are suppressed. */
  onDecode: (code: string) => void;
}

const DEBOUNCE_MS = 1200;

/**
 * Camera + ZXing barcode scanner with pause/resume.
 *
 * Perf knobs (do NOT regress — see M2.7): ZXing lib loaded in PARALLEL with
 * getUserMedia; resolution capped 1280×720; POSSIBLE_FORMATS = [EAN_13, UPC_A,
 * EAN_8]; NO TRY_HARDER; delayBetweenScanAttempts: 120; manual play() only
 * (NO autoPlay — iOS Safari AbortError trap).
 *
 * pause() stops invoking onDecode but keeps the MediaStream + controls alive so
 * resume() re-arms instantly (no second getUserMedia → no iOS AbortError, no
 * camera warm-up). resume(code) re-seeds the DEBOUNCE_MS dedupe with the
 * just-handled code so the same barcode still in-frame is NOT re-fired (no
 * duplicate confirm card / miss loop); the window survives resume by design.
 * Teardown is via unmount (effect cleanup); retry() re-runs init.
 */
export function useBarcodeScanner({ onDecode }: Options) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [state, setState] = useState<ScannerState>({ kind: "idle" });
  const [attempt, setAttempt] = useState(0);
  // Torch (flashlight): supported on devices whose camera track reports the
  // capability (iOS 17.4+/Android Chrome). The button only renders when the
  // capability is really there, so unsupported devices see nothing.
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  // Mutable control flags shared with the decode callback (refs so the running
  // effect sees live values without re-subscribing).
  const pausedRef = useRef(false);
  const lastCodeRef = useRef<{ code: string; at: number } | null>(null);
  const onDecodeRef = useRef(onDecode);
  onDecodeRef.current = onDecode;

  const pause = () => { pausedRef.current = true; };
  /**
   * Re-arm the decoder. The DEBOUNCE_MS window MUST survive resume(): the
   * barcode just handled is almost always still sitting in the camera frame, so
   * the very next decoded frame would re-fire the SAME code and produce a
   * duplicate confirm card / unwanted miss loop. We therefore do NOT null
   * lastCodeRef. Pass the code that was just handled so its debounce timestamp
   * is refreshed to "now" — the same in-frame barcode stays suppressed for
   * DEBOUNCE_MS, while a genuinely re-presented product (after the window, or a
   * different barcode) still fires. This reconciles "allow intentional re-scan"
   * with "never double-log": intentional re-scan works once the user re-presents
   * the item or waits ~1.2s; an unmoved barcode in-frame does not loop.
   */
  const resume = (justHandledCode?: string) => {
    pausedRef.current = false;
    if (justHandledCode) lastCodeRef.current = { code: justHandledCode, at: Date.now() };
    // else: keep the existing lastCodeRef so the debounce window still applies.
  };
  const retry = () => { setState({ kind: "idle" }); setAttempt((a) => a + 1); };

  const toggleTorch = async () => {
    const tr = trackRef.current;
    if (!tr) return;
    const next = !torchOn;
    try {
      await tr.applyConstraints({ advanced: [{ torch: next } as MediaTrackConstraintSet] });
      setTorchOn(next);
    } catch {
      // The capability lied (or the track died) — drop the button.
      setTorchSupported(false);
      setTorchOn(false);
    }
  };

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let controls: { stop: () => void } | null = null;
    let torchProbe: ReturnType<typeof setTimeout> | null = null;
    pausedRef.current = false;
    lastCodeRef.current = null;
    trackRef.current = null;
    setTorchSupported(false);
    setTorchOn(false);

    (async () => {
      try {
        const zxingP = loadZXing();
        const libP = import("@zxing/library");

        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (stopped || !videoRef.current) {
          stream?.getTracks().forEach((tr) => tr.stop());
          return;
        }
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setState({ kind: "scanning" });

        // Torch capability — some engines only report it a beat after play().
        const track = stream.getVideoTracks()[0] ?? null;
        trackRef.current = track;
        const probeTorch = () => {
          const caps = track?.getCapabilities?.() as { torch?: boolean } | undefined;
          if (caps?.torch) setTorchSupported(true);
        };
        probeTorch();
        torchProbe = setTimeout(() => { if (!stopped) probeTorch(); }, 700);

        const [{ BrowserMultiFormatReader }, lib] = await Promise.all([zxingP, libP]);
        const hints = new Map<number, unknown>([
          [lib.DecodeHintType.POSSIBLE_FORMATS, [
            lib.BarcodeFormat.EAN_13,
            lib.BarcodeFormat.UPC_A,
            lib.BarcodeFormat.EAN_8,
          ]],
        ]);
        const reader = new BrowserMultiFormatReader(hints as never, {
          delayBetweenScanAttempts: 120,
        });
        controls = await reader.decodeFromVideoElement(
          videoRef.current,
          (result) => {
            if (stopped || pausedRef.current || !result) return;
            const norm = normalizeCode(result.getText());
            if (!norm) return;
            const now = Date.now();
            const last = lastCodeRef.current;
            if (last && last.code === norm && now - last.at < DEBOUNCE_MS) return;
            lastCodeRef.current = { code: norm, at: now };
            onDecodeRef.current(norm);
          }
        );
      } catch (e) {
        if (stopped) return;
        const name = (e as DOMException)?.name;
        if (name === "NotAllowedError")  setState({ kind: "denied" });
        else if (name === "NotFoundError") setState({ kind: "no_camera" });
        else setState({ kind: "error", message: (e as Error).message });
      }
    })();

    return () => {
      stopped = true;
      if (torchProbe) clearTimeout(torchProbe);
      try { controls?.stop(); } catch { /* ignore */ }
      stream?.getTracks().forEach((tr) => tr.stop());
    };
  }, [attempt]);

  // No imperative stop(): teardown is driven by unmounting the hook's host
  // component (the effect cleanup above stops controls + tracks). Do NOT declare
  // an unused `const stop` here — with noUnusedLocals it's a hard TS6133 build
  // failure, not a lint warning.
  return { videoRef, state, pause, resume, retry, torchSupported, torchOn, toggleTorch };
}
